import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  addOrUpdateImageDocument,
  ImageHandlingState,
  updateImageDocumentInDict,
  setUpdatingImageMetadataInProgress,
  removeImageDocumentFromSpecificContext,
} from '../imageHandlingV2.slice';
import {
  ImageDocumentStoreItem,
  ODataDocument,
  UpdateDocumentMetadataSignalRResponse,
} from '../../../../components/v2/components/imageHandling/types/types';
import { AppDispatch, RootState } from '@store/types';
import { parseAndAddUrlsToImagesDocuments } from '@reducers/v2/imageHandlingV2/thunks';
import { getDocumentById } from '@reducers/search';
import { refreshMachineImageAfterUpload, clearMachineImageByDocumentId } from '@reducers/machines';
import type { Machine } from '@reducers/machines/types';
import { KEYS_TAGS } from '@components/imageHandling/common';

export const updateImageMetadataMessageHandler = createAsyncThunk<
  void,
  UpdateDocumentMetadataSignalRResponse,
  { dispatch: AppDispatch; state: RootState }
>('imageHandlingV2/updateImageMetadataMessageHandler', async (payload, { dispatch, getState }) => {
  const rootState = getState() as { imageHandlingV2: ImageHandlingState };
  const state = rootState.imageHandlingV2;

  const documentLinks = payload.links || [];

  // Add image to the link context
  documentLinks.forEach(async (l) => {
    if (!l.isActivated) {
      return;
    }
    const imageContext = { scopeId: l.scopeId, scopeReferenceId: l.scopeReferenceId };

    const newImage: ODataDocument[] = await dispatch(getDocumentById(payload.documentId));

    if (newImage?.length) {
      // Parse the document with URLs before returning
      const parsedResult = await dispatch(
        parseAndAddUrlsToImagesDocuments({
          imagesDocuments: [newImage[0]],
        })
      );

      if (parseAndAddUrlsToImagesDocuments.fulfilled.match(parsedResult)) {
        const parsedData = parsedResult.payload?.[0];
        if (parsedData) {
          dispatch(addOrUpdateImageDocument({ document: parsedData, imageContext }));

          if (Number(l.scopeId) === KEYS_TAGS.Machine) {
            dispatch(
              refreshMachineImageAfterUpload({
                machineId: Number(l.scopeReferenceId),
                optimisticImage: parsedData as NonNullable<Machine['machineImage']>,
              })
            );
          }
        }
      }
    }
  });

  // Get the document directly from the dictionary
  const foundDocument = state.imagesDocumentsDict[payload.documentId];

  if (foundDocument) {
    // Update links array
    const currentLinks = foundDocument.links || [];
    let updatedLinks = [...currentLinks];

    if (!currentLinks.length) {
      updatedLinks = documentLinks;
    } else {
      documentLinks.forEach((link) => {
        const existedLinkIndex = updatedLinks.findIndex((l) => l.scopeId === link.scopeId && l.scopeReferenceId === link.scopeReferenceId);

        // This means link has being added
        if (existedLinkIndex === -1 && link.isActivated) {
          updatedLinks.push(link);
        }

        // This means link has being removed
        if (!link.isActivated) {
          dispatch(
            removeImageDocumentFromSpecificContext({
              documentId: foundDocument.documentId,
              imageContext: { scopeId: link.scopeId, scopeReferenceId: link.scopeReferenceId },
            })
          );
          const removedMachineId = Number(link.scopeReferenceId);
          if (Number(link.scopeId) === KEYS_TAGS.Machine) {
            dispatch(clearMachineImageByDocumentId({ documentId: foundDocument.documentId, machineId: removedMachineId }));
          }
          updatedLinks = updatedLinks.filter((_, index) => index !== existedLinkIndex);
        }
      });
    }

    // Create updated document
    const updatedImageDocument = {
      ...foundDocument,
      links: updatedLinks,
    } as ImageDocumentStoreItem;

    // Dispatch the update action to update in ALL contexts
    dispatch(
      updateImageDocumentInDict({
        documentId: payload.documentId,
        updatedImageDocument,
      })
    );
  }

  // Set updating progress to false
  dispatch(setUpdatingImageMetadataInProgress(false));
});
